import { onboardingApi } from '@/services/api';
import {
  Onboarding, OnboardingDocumentUploadPayload, Registration,
} from '@/services/requests/onboarding/Onboarding.types';
import { IApiResponse } from '@/types/interfaces/ApiResponse.interface';
import {
  BusinessActorDocument,
  CorporateDocument,
  CorporateDocumentType,
  DocumentSide,
} from '@/types/interfaces/Document.interface';
import { OtpConfirmationResponse } from '@/types/interfaces/Otp';
import {
  SDKPaginationRequestOptions,
  transformToOnboardingPaginationRequest,
} from '@/types/interfaces/PaginationRequest.interface';
import { PaginationResponse, SDKPaginationResponse, transformOnboardingPaginationResponse } from '@/types/interfaces/PaginationResponse.interface';
import { getCleanedPayload } from '@/utils/getCleanedPayload';

import { IAuthData } from '../auth/Auth.types';

function createAdminRequests<T>(path: string) {
  return {
    getApplication(id?: string): Promise<IApiResponse<T>> {
      return onboardingApi.get(`/admin/applications/${path}/${id}`);
    },
    addCustomerManual(data: Onboarding.ManualCustomer): Promise<IApiResponse<T>> {
      return onboardingApi.post(`/admin/applications/${path}`, data);
    },
    updateStatus(id: string, status: Onboarding.UpdateApplicationStatus): Promise<IApiResponse<T>> {
      return onboardingApi.patch(`/admin/applications/${path}/${id}/${status}`);
    },
    verifyApplication(id: string): Promise<IApiResponse<T>> {
      return onboardingApi.post(`/admin/applications/${path}/${id}/verify`);
    },
    declineApplication(id: string): Promise<IApiResponse<T>> {
      return onboardingApi.post(`/admin/applications/${path}/${id}/decline`);
    },
    inProgressApplication(id: string): Promise<IApiResponse<T>> {
      return onboardingApi.patch(`/admin/applications/${path}/${id}/in-progress`);
    },
    noResponseApplication(id: string): Promise<IApiResponse<T>> {
      return onboardingApi.post(`/admin/applications/${path}/${id}/no-response`);
    },
    onNonVerifiedApplication(id: string): Promise<IApiResponse<T>> {
      return onboardingApi.post(`/admin/applications/${path}/${id}/non-verified`);
    },
    onOnboardingApplication(id: string): Promise<IApiResponse<T>> {
      return onboardingApi.post(`/admin/applications/${path}/${id}/onboarding`);
    },
    updateContract(id: string, contractId: string): Promise<IApiResponse<T>> {
      return onboardingApi.patch(`/admin/applications/${path}/${id}/contract`, { id: contractId });
    },
    updateRiskLevel(id: string, riskLevel: string): Promise<IApiResponse<T>> {
      return onboardingApi.patch(`/admin/applications/${path}/${id}/risk-level`, { riskLevel });
    },
  };
}

export const OnboardingAdminRequests = {
  Business: {
    ...createAdminRequests<Onboarding.Business.FullApplication>('corporate'),

    updateCompanyDetails(id: string, data: Onboarding.Business.CompanyDetails): Promise<IApiResponse<Onboarding.Business.FullApplication>> {
      return onboardingApi.post(`/admin/applications/corporate/${id}/company-details`, getCleanedPayload(data));
    },
    getCorporateDocument(id: string, type: CorporateDocumentType): Promise<IApiResponse<Blob>> {
      return onboardingApi.get(`/admin/applications/corporate/${id}/corporate-docs/document`, {
        params: {
          type,
        },
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream, application/json',
        },
      });
    },
    uploadCorporateDocument(data: OnboardingDocumentUploadPayload): Promise<IApiResponse<Onboarding.Business.FullApplication>> {
      return onboardingApi.post(`/admin/applications/corporate/${data.id}/corporate-docs/document`, data.formData, {
        params: {
          type: data.type,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    getReviewedDocument(userId: string, imageId: string): Promise<IApiResponse<Blob>> {
      return onboardingApi.get(`/admin/applications/corporate/${userId}/corporate-docs/reviewed/${imageId}`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream, application/json',
        },
      });
    },
    addBusinessActor(id: string, actorData: Onboarding.Business.Actor): Promise<IApiResponse<any>> {
      return onboardingApi.post(`/admin/applications/corporate/${id}/business-actors`, actorData);
    },
    getBusinessDocument(data: OnboardingDocumentUploadPayload, actorId: string): Promise<IApiResponse<Blob>> {
      return onboardingApi.get(`/admin/applications/corporate/${data.id}/business-actors/${actorId}/document`, {
        params: {
          type: data.type,
          side: data.side,
        },
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream, application/json',
        },
      });
    },
    uploadBusinessDocument(data: OnboardingDocumentUploadPayload, actorId: string) {
      return onboardingApi.post(`/admin/applications/corporate/${data.id}/business-actors/${actorId}/document`, data.formData, {
        params: {
          type: data.type,
          issuingCountryIso3: data.issuingCountryIso3,
          side: data.side,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    deleteBusinessActor(id: string, actorId: string) {
      return onboardingApi.delete(`/admin/applications/corporate/${id}/business-actors/${actorId}`);
    },
    updateBusinessActor(id: string, actorId: string, data: Onboarding.Business.Actor) {
      return onboardingApi.patch(`/admin/applications/corporate/${id}/business-actors/${actorId}`, data);
    },
  },
  Personal: {
    ...createAdminRequests<Onboarding.Personal.Application>('personal'),

    updatePersonalDetails(id: string | string[], data: Onboarding.Personal.PersonalDetails): Promise<IApiResponse<Onboarding.Personal.Application>> {
      return onboardingApi.patch(`/admin/applications/personal/${id}/personal-details`, getCleanedPayload(data));
    },
    getPersonalDocument(id: string | string[], params: {type: CorporateDocumentType; side: DocumentSide}): Promise<IApiResponse<Blob>> {
      return onboardingApi.get(`/admin/applications/personal/${id}/personal-docs/document`, {
        params,
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream, application/json',
        },
      });
    },
    uploadPersonalDocument(id: string | string[], data: OnboardingDocumentUploadPayload): Promise<IApiResponse<Onboarding.Personal.Application>> {
      return onboardingApi.post(`/admin/applications/personal/${id}/personal-docs/document`, data.formData, {
        params: {
          type: data.type,
          side: data.side,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    getReviewedDocument(userId: string, imageId: string): Promise<IApiResponse<Blob>> {
      return onboardingApi.get(`/admin/applications/personal/${userId}/personal-docs/reviewed/${imageId}`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream, application/json',
        },
      });
    },
  },
  SoleTrader: {
    ...createAdminRequests<Onboarding.SoleTrader.FullApplication>('sole-trader'),

    updatePersonalDetails(id: string, data: Onboarding.SoleTrader.Details): Promise<IApiResponse<Onboarding.SoleTrader.FullApplication>> {
      return onboardingApi.patch(`/admin/applications/sole-trader/${id}/details`, getCleanedPayload(data));
    },
    updateBusinessInfo(id: string, data: Onboarding.SoleTrader.BusinessInfo): Promise<IApiResponse<Onboarding.SoleTrader.FullApplication>> {
      return onboardingApi.patch(`/admin/applications/sole-trader/${id}/business-info`, getCleanedPayload(data));
    },
    document: {
      get(id: string, params: { type: CorporateDocumentType; side: DocumentSide }): Promise<IApiResponse<Blob>> {
        return onboardingApi.get(`/admin/applications/sole-trader/${id}/docs/document`, {
          params,
          responseType: 'blob',
          headers: {
            Accept: 'application/octet-stream, application/json',
          },
        });
      },
      upload(id: string, data: OnboardingDocumentUploadPayload): Promise<IApiResponse<Onboarding.SoleTrader.FullApplication>> {
        return onboardingApi.post(`/admin/applications/sole-trader/${id}/docs/document`, data.formData, {
          params: {
            type: data.type,
            side: data.side,
          },
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
      },
    },
    getReviewedDocument(userId: string, imageId: string): Promise<IApiResponse<Blob>> {
      return onboardingApi.get(`/admin/applications/sole-trader/${userId}/docs/reviewed/${imageId}`, {
        responseType: 'blob',
        headers: {
          Accept: 'application/octet-stream, application/json',
        },
      });
    },
  },
  Shared: {
    async getApplications(options: SDKPaginationRequestOptions<any, any>): Promise<Onboarding.Employee.ApplicationResponse> {
      const res = await onboardingApi.get('/admin/applications', {
        params: transformToOnboardingPaginationRequest(options),
      }) as IApiResponse<PaginationResponse<Onboarding.Employee.Application>>;

      if (res.error) return res;

      return {
        ...res,
        response: transformOnboardingPaginationResponse(res.response),
      };
    },
    blockApplication(applicationId: string): Promise<IApiResponse<{ blocked: boolean }>> {
      return onboardingApi.post(`/admin/applications/${applicationId}/block`);
    },
    unblockApplication(applicationId: string): Promise<IApiResponse<{ blocked: boolean }>> {
      return onboardingApi.post(`/admin/applications/${applicationId}/unblock`);
    },
    updateUserContact(userId: string, data: Record<string, string | undefined>) {
      return onboardingApi.patch(`/admin/applications/${userId}/contact`, data);
    },
    closeAccount(applicationId: string): Promise<IApiResponse<{ applicationStatus: Onboarding.ApplicationStatus }>> {
      return onboardingApi.post(`/admin/applications/${applicationId}/close`);
    },
    comments: {
      async get(
        applicationId: string,
        pagination: SDKPaginationRequestOptions<unknown, unknown>,
      ): Promise<IApiResponse<SDKPaginationResponse<Onboarding.Comment>>> {
        const res = await onboardingApi.get(`/admin/applications/${applicationId}/comments`, {
          params: transformToOnboardingPaginationRequest(pagination),
        }) as IApiResponse<PaginationResponse<Onboarding.Comment>>;

        if (res.error) return res;

        return {
          ...res,
          response: transformOnboardingPaginationResponse(res.response),
        };
      },
      add(applicationId: string, text: string): Promise<IApiResponse<Onboarding.Comment>> {
        return onboardingApi.post(`/admin/applications/${applicationId}/comments`, { text });
      },
      edit(applicationId: string, commentId: string, text: string): Promise<IApiResponse<Onboarding.Comment>> {
        return onboardingApi.patch(`/admin/applications/${applicationId}/comments/${commentId}`, { text });
      },
      remove(applicationId: string, commentId: string): Promise<IApiResponse<Onboarding.Comment>> {
        return onboardingApi.delete(`/admin/applications/${applicationId}/comments/${commentId}`);
      },
    },
  },
};

export const OnboardingUserRequests = {
  // Registration

  register(data: Registration.Payload, captcha: string): Promise<IApiResponse<OtpConfirmationResponse>> {
    return onboardingApi.post('/register', data, {
      headers: {
        'X-ReCaptcha-Token': captcha,
      },
    });
  },

  resendOtp(email: string): Promise<IApiResponse<OtpConfirmationResponse>> {
    return onboardingApi.post('/register/resend-otp', { email });
  },

  confirmEmail(data: Registration.ConfirmEmailPayload): Promise<IApiResponse<IAuthData>> {
    return onboardingApi.post('/register/confirm-email', data);
  },

  setPhone(phone: string, captcha: string): Promise<IApiResponse<OtpConfirmationResponse>> {
    return onboardingApi.post('/register/phone', { phone }, {
      headers: {
        'X-ReCaptcha-Token': captcha,
      },
    });
  },

  resendOtpToPhone(phone: string): Promise<IApiResponse<OtpConfirmationResponse>> {
    return onboardingApi.post('/register/phone/resend-otp', { phone });
  },

  confirmPhone(data: Registration.ConfirmPhonePayload): Promise<IApiResponse<IAuthData>> {
    return onboardingApi.post('/register/phone/confirm', data);
  },
};

export const OnboardingBusinessRequests = {
  getApplication(preventRefreshTokenAndInactivityTimeout: boolean = false): Promise<IApiResponse<Onboarding.Business.Application>> {
    return onboardingApi.get('/application/corporate', {
      meta: { preventRefreshTokenAndInactivityTimeout },
    });
  },

  companyDetails: {
    update(data: Onboarding.Business.CompanyDetails): Promise<IApiResponse<Onboarding.Business.Application>> {
      return onboardingApi.post('/application/corporate/company-details', getCleanedPayload(data));
    },
    complete(): Promise<IApiResponse<Onboarding.Business.Application>> {
      return onboardingApi.post('/application/corporate/company-details/complete');
    },
  },

  accountManager: {
    add(personalInfo?: Onboarding.Business.ActorInfo): Promise<IApiResponse<Onboarding.Business.Actor>> {
      return onboardingApi.post('/application/corporate/account-manager', { personalInfo });
    },
    getDocument(data: Onboarding.Business.Payload.GetManagerDocument) {
      return onboardingApi.get(`/application/corporate/account-manager/${data.id}/document`, {
        params: {
          type: data.type,
          side: data.side,
        },
        responseType: 'blob',
      });
    },
    uploadDocument(data: Onboarding.Business.Payload.UploadManagerDocument): Promise<IApiResponse<Onboarding.Business.Application>> {
      const params: Partial<Onboarding.Business.Payload.UploadManagerDocument> = {
        type: data.type,
      };
      if (data?.side) params.side = data.side;
      if (data?.issuingCountryIso3) params.issuingCountryIso3 = data.issuingCountryIso3;
      return onboardingApi.post(`/application/corporate/account-manager/${data.id}/document`, data.formData, {
        params,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    complete(): Promise<IApiResponse<Onboarding.Business.Application>> {
      return onboardingApi.post('/application/corporate/account-manager/complete');
    },
  },

  businessActors: {
    add(data: Onboarding.Business.Payload.AddBusinessActor): Promise<IApiResponse<Onboarding.Business.Actor>> {
      return onboardingApi.post('/application/corporate/business-actors', data);
    },
    update(id: string, data: Onboarding.Business.ActorPayload) {
      return onboardingApi.put(`/application/corporate/business-actors/${id}`, data);
    },
    delete(id: string): Promise<IApiResponse<Onboarding.Business.Actor>> {
      return onboardingApi.delete(`/application/corporate/business-actors/${id}`);
    },
    getDocument(data: Onboarding.Business.Payload.GetActorDocument) {
      return onboardingApi.get(`/application/corporate/business-actors/${data.id}/document`, {
        params: {
          type: data.type,
          side: data.side,
        },
        responseType: 'blob',
      });
    },
    uploadDocument(data: Onboarding.Business.Payload.UploadActorDocument): Promise<IApiResponse<Onboarding.Business.Application>> {
      const params: Partial<Onboarding.Business.Payload.UploadActorDocument> = {
        type: data.type,
      };
      if (data?.side) params.side = data.side;
      if (data?.issuingCountryIso3) params.issuingCountryIso3 = data.issuingCountryIso3;
      return onboardingApi.post(`/application/corporate/business-actors/${data.id}/document`, data.formData, {
        params,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    complete(): Promise<IApiResponse<Onboarding.Business.Application>> {
      return onboardingApi.post('/application/corporate/business-actors/complete');
    },
  },

  corporateDocs: {
    getDocument(type: CorporateDocument) {
      return onboardingApi.get('/application/corporate/corporate-docs/document', {
        params: { type },
        responseType: 'blob',
      });
    },
    uploadDocument(data: Onboarding.Business.Payload.UploadCorpDocument): Promise<IApiResponse<Onboarding.Business.Application>> {
      return onboardingApi.post('/application/corporate/corporate-docs/document', data.formData, {
        params: {
          type: data.type,
        },
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    complete(): Promise<IApiResponse<Onboarding.Business.Application>> {
      return onboardingApi.post('/application/corporate/corporate-docs/complete');
    },
  },

  getLivenessCheckIFrame(): Promise<IApiResponse<Onboarding.LivenessCheckIFrame>> {
    return onboardingApi.get('/application/corporate/liveness-check', {
      meta: {
        preventRefreshTokenAndInactivityTimeout: true,
      },
    });
  },
};

export const OnboardingPersonalRequests = {
  getApplication(preventRefreshTokenAndInactivityTimeout: boolean = false): Promise<IApiResponse<Onboarding.Personal.Application>> {
    return onboardingApi.get('/application/personal', {
      meta: { preventRefreshTokenAndInactivityTimeout },
    });
  },

  updatePersonalDetails(data: Onboarding.Personal.PersonalDetails): Promise<IApiResponse<Onboarding.Personal.Application>> {
    return onboardingApi.post('/application/personal/personal-details', getCleanedPayload(data));
  },

  completePersonalDetails(): Promise<IApiResponse<Onboarding.Personal.Application>> {
    return onboardingApi.post('application/personal/personal-details/complete');
  },

  getDocument(type: BusinessActorDocument, side: DocumentSide) {
    return onboardingApi.get('/application/personal/personal-docs/document', {
      params: { type, side },
      responseType: 'blob',
    });
  },
  uploadDocument(data: Onboarding.Personal.Payload.UploadDocument): Promise<IApiResponse<Onboarding.Personal.Application>> {
    const params: Partial<Onboarding.Personal.Payload.UploadDocument> = {
      type: data.type,
    };
    if (data?.side) params.side = data.side;
    if (data?.issuingCountryIso3) params.issuingCountryIso3 = data.issuingCountryIso3;
    return onboardingApi.post('/application/personal/personal-docs/document', data.formData, {
      params,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  getLivenessCheckIFrame(): Promise<IApiResponse<Onboarding.LivenessCheckIFrame>> {
    return onboardingApi.get('/application/personal/liveness-check', {
      meta: { preventRefreshTokenAndInactivityTimeout: true },
    });
  },

  acceptContractPricing(): Promise<IApiResponse<Onboarding.Personal.Application>> {
    return onboardingApi.post('/application/personal/pricing/accept');
  },
};

export const OnboardingSoleTraderRequests = {
  getApplication(preventRefreshTokenAndInactivityTimeout: boolean = false): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
    return onboardingApi.get('/application/sole-trader', {
      meta: { preventRefreshTokenAndInactivityTimeout },
    });
  },

  personalDetails: {
    update(data: Onboarding.SoleTrader.Details): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
      return onboardingApi.post('/application/sole-trader/details', getCleanedPayload(data));
    },
    complete(): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
      return onboardingApi.post('/application/sole-trader/details/complete');
    },
  },

  businessInfo: {
    update(data: Onboarding.SoleTrader.BusinessInfo): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
      return onboardingApi.post('/application/sole-trader/business-info', getCleanedPayload(data));
    },
    complete(): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
      return onboardingApi.post('/application/sole-trader/business-info/complete');
    },
  },

  document: {
    get(type: BusinessActorDocument, side: DocumentSide): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
      return onboardingApi.get('/application/sole-trader/docs/document', {
        params: { type, side },
        responseType: 'blob',
      });
    },
    upload(data: Onboarding.SoleTrader.Payload.UploadDocument): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
      const params: Partial<Onboarding.SoleTrader.Payload.UploadDocument> = {
        type: data.type,
      };
      if (data?.side) params.side = data.side;
      if (data?.issuingCountryIso3) params.issuingCountryIso3 = data.issuingCountryIso3;
      return onboardingApi.post('/application/sole-trader/docs/document', data.formData, {
        params,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
  },

  acceptPricing(): Promise<IApiResponse<Onboarding.SoleTrader.Application>> {
    return onboardingApi.post('/application/sole-trader/pricing/accept');
  },

  getLivenessCheckIFrame(): Promise<IApiResponse<Onboarding.LivenessCheckIFrame>> {
    return onboardingApi.get('/application/sole-trader/liveness-check', {
      meta: { preventRefreshTokenAndInactivityTimeout: true },
    });
  },
};

export const OnboardingCommonRequests = {
  getCountries(): Promise<IApiResponse<Onboarding.Country[]>> {
    return onboardingApi.get('/countries');
  },
};
